import { facebookUrl, instagramUrl, twitterUrl, youtubeUrl } from "@features/footer/footerConstant";
import { useTranslation } from "react-i18next";
import { BRAND_NAME, WORDPRESS_URL } from "@constants";
import { Grid, IconButton, Link, Typography } from "@mui/material";
import { LabeledButton } from "@common/LabeledButton/LabeledButton";
import { useStyle } from "@features/footer/style";
import { FONT_WEIGHTS } from "../../theme/fontWeights";
import facebookIconImg from "@static/icons/footer/FacebookIcon.png";
import instagramIconImg from "@static/icons/footer/InstagramIcon.png";
import youtubeIconImg from "@static/icons/footer/YouTubeIcon.png";
import XTwitterIcon from "@static/icons/footer/XTwitterIcon.svg";

export const Footer = () => {
    const { t } = useTranslation("footer");
    const { classes } = useStyle();

    const helpSupportLinks = [
        {
            title: t("ourCoverageMap"),
            url: `${WORDPRESS_URL}/coverage/`,
        },
        {
            title: t("roaming"),
            url: `${WORDPRESS_URL}/our-roaming-rates/`,
        },
        {
            title: t("contactUs"),
            url: `${WORDPRESS_URL}/contact/`,
        },
        {
            title: t("topFAQs"),
            url: `${WORDPRESS_URL}/help-support/`,
        },
    ];

    const informationsLinks = [
        {
            title: t("pricing"),
            url: `${WORDPRESS_URL}/pricing/`,
        },
        {
            title: t("terms"),
            url: `${WORDPRESS_URL}/terms/`,
        },
        {
            title: t("legal"),
            url: `${WORDPRESS_URL}/legal/`,
        },
        {
            title: t("accessibility"),
            url: `${WORDPRESS_URL}/accessibility/`,
        },
        {
            title: t("childSafety"),
            url: `${WORDPRESS_URL}/child-safety/`,
        },
        {
            title: t("privacy"),
            url: `${WORDPRESS_URL}/privacy/`,
        },
        {
            title: t("cookies"),
            url: `${WORDPRESS_URL}/cookies/`,
        },
        {
            title: t("codePractice"),
            url: `${WORDPRESS_URL}/code-of-practice/`,
        },
    ];

    const footerLinks = (links: { title: string; url: string }[]): JSX.Element[] => {
        return links.map(({ url, title }) => (
            <Typography data-testid="footer-link" key={title} variant="small">
                <Link color="primary" href={url} underline="hover">
                    {title}
                </Link>
            </Typography>
        ));
    };

    return (
        <footer className={classes.footer}>
            <Grid alignItems="flex-start" className={classes.box} container>
                <Grid className={classes.linkColumns} container item xs>
                    <Grid item xs>
                        <Typography>Help & Support</Typography>
                        <div className={classes.linksContainer}>{footerLinks(helpSupportLinks)}</div>
                    </Grid>
                    <Grid item xs>
                        <Typography>Information</Typography>
                        <div className={classes.linksContainer}>{footerLinks(informationsLinks)}</div>
                    </Grid>
                </Grid>
                <Grid className={classes.brandColumn} item xs>
                    <div className={classes.brand}>
                        <IconButton
                            aria-label={t("open-gomo-youtube")}
                            className={classes.brandIconButton}
                            color="secondary"
                            href={youtubeUrl}
                            rel="noreferrer"
                            target="_blank"
                        >
                            <span
                                aria-hidden="true"
                                className={classes.brandIcon}
                                style={{
                                    maskImage: `url(${youtubeIconImg})`,
                                    WebkitMaskImage: `url(${youtubeIconImg})`,
                                }}
                            />
                        </IconButton>
                        <IconButton
                            aria-label={t("open-gomo-instagram")}
                            className={classes.brandIconButton}
                            color="secondary"
                            href={instagramUrl}
                            rel="noreferrer"
                            target="_blank"
                        >
                            <span
                                aria-hidden="true"
                                className={classes.brandIcon}
                                style={{
                                    maskImage: `url(${instagramIconImg})`,
                                    WebkitMaskImage: `url(${instagramIconImg})`,
                                }}
                            />
                        </IconButton>
                        <IconButton
                            aria-label={t("open-gomo-facebook")}
                            className={classes.brandIconButton}
                            color="secondary"
                            href={facebookUrl}
                            rel="noreferrer"
                            target="_blank"
                        >
                            <span
                                aria-hidden="true"
                                className={classes.brandIcon}
                                style={{
                                    maskImage: `url(${facebookIconImg})`,
                                    WebkitMaskImage: `url(${facebookIconImg})`,
                                }}
                            />
                        </IconButton>
                        <IconButton
                            aria-label={t("open-gomo-x")}
                            className={classes.brandIconButton}
                            color="secondary"
                            href={twitterUrl}
                            rel="noreferrer"
                            target="_blank"
                        >
                            <XTwitterIcon aria-hidden="true" className={classes.brandSvgIcon} />
                        </IconButton>
                    </div>
                    <div className={classes.bottom}>
                        <div className={classes.cookie}>
                            <LabeledButton
                                className={classes.cookieButton}
                                color="secondary"
                                labelText={t("cookieSettings")}
                                onClick={() => window.OneTrust?.ToggleInfoDisplay()}
                            >
                                {t("cookieSettings")}
                            </LabeledButton>
                        </div>
                        <div className={classes.copyright}>
                            <Typography fontSize={12} fontWeight={FONT_WEIGHTS.medium}>
                                &#169; {new Date().getFullYear()} {t("allRightsReserved", { brand: BRAND_NAME })}
                            </Typography>
                        </div>
                    </div>
                </Grid>
            </Grid>
        </footer>
    );
};
